import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  CarWheelPositionText,
  TireSeason,
  type TireQuoteCustomerLeadRecord,
  type TreadPositionReadingRecord,
  type TreadWearRecord
} from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { NavioInfoCard, NoddiButton, NoddiSheet, useIsMobile } from 'noddi-ui-web';
import {
  CustomDateFormats,
  noddiFormatDate,
  treadDepthTone,
  type TreadDepthThresholds,
  type TreadDepthTone
} from 'noddi-util';
import { formatMm } from './formatMm';
import { getTireLeadReasonLabel, getTirePositionLabel } from './tireLeadLabels';
import type { TireLeadReasonCopy } from './tireLeadReasonCopy';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  copy: TireLeadReasonCopy;
  lead: TireQuoteCustomerLeadRecord | null;
  treadWear: TreadWearRecord | null;
  thresholds: TreadDepthThresholds | null;
  season: TireSeason;
  isWornTread: boolean;
  onSeeOptions: () => void;
};

// Read like the car from above: front pair, then rear pair, left before right.
const POSITION_ORDER: CarWheelPositionText[] = [
  CarWheelPositionText.FRONT_LEFT,
  CarWheelPositionText.FRONT_RIGHT,
  CarWheelPositionText.REAR_LEFT,
  CarWheelPositionText.REAR_RIGHT
];

// The tread bar's zone colors: red under the legal minimum, amber under the recommended one.
const TONE_DOT_CLASS: Record<TreadDepthTone, string> = {
  good: 'bg-success',
  soon: 'bg-warning',
  worn: 'bg-danger'
};

/** Why the tires should be replaced: the limits against the readings for worn tread, the findings otherwise. */
export const TireOfferWhySheet = ({
  open,
  onOpenChange,
  copy,
  lead,
  treadWear,
  thresholds,
  season,
  isWornTread,
  onSeeOptions
}: Props) => {
  const isMobile = useIsMobile();

  return (
    <NoddiSheet
      open={open}
      onOpenChange={onOpenChange}
      side={isMobile ? 'bottom' : 'right'}
      title={isWornTread ? t`Why do we recommend new tires?` : copy.headline}
      description={copy.whyIntro}
      footer={
        <NoddiButton className='w-full' onClick={onSeeOptions}>
          <Trans>See tire options</Trans>
        </NoddiButton>
      }
    >
      <div className='flex flex-col gap-5'>
        {isWornTread && treadWear && <TireMeasurements treadWear={treadWear} thresholds={thresholds} season={season} />}
        {!isWornTread && lead && (
          <NavioInfoCard
            density='compact'
            items={lead.tireDetails.positions.map((position) => ({
              label: getTirePositionLabel(position.position),
              value: position.reasons.map(getTireLeadReasonLabel).join(', '),
              copyText: false as const
            }))}
          />
        )}
        <ol className='flex flex-col gap-4'>
          {copy.whyPoints.map((point, index) => (
            <li key={point.title} className='flex items-start gap-4'>
              <span className='flex size-9 flex-none items-center justify-center rounded-lg bg-lightPurple font-bold text-darkPurple'>
                {index + 1}
              </span>
              <div className='flex flex-col gap-0.5'>
                <span className='font-semibold'>{point.title}</span>
                <span className='text-sm text-muted-foreground'>{point.description}</span>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </NoddiSheet>
  );
};

const getLegalLimitLabel = (season: TireSeason) => {
  switch (season) {
    case TireSeason.SUMMER:
      return t`Legal limit for summer tires`;
    case TireSeason.WINTER:
      return t`Legal limit for winter tires in the winter season`;
    case TireSeason.ALL_SEASON:
      return t`Legal limit`;
  }
};

const getRecommendedMinimumLabel = (season: TireSeason) => {
  switch (season) {
    case TireSeason.SUMMER:
      return t`Recommended minimum for summer tires`;
    case TireSeason.WINTER:
      return t`Recommended minimum for winter tires`;
    case TireSeason.ALL_SEASON:
      return t`Recommended minimum`;
  }
};

/**
 * Every measured tire's latest tread depth under the date of the latest measurement, then the season's limits they
 * are graded against, dotted in the colors the readings take below them.
 */
const TireMeasurements = ({
  treadWear,
  thresholds,
  season
}: {
  treadWear: TreadWearRecord;
  thresholds: TreadDepthThresholds | null;
  season: TireSeason;
}) => {
  const readings = [...treadWear.latestReadings].sort((a, b) => positionRank(a) - positionRank(b));
  const latestAt = readings
    .map((reading) => reading.measuredAt)
    .sort()
    .at(-1);
  const measuredOn = latestAt ? noddiFormatDate(latestAt, CustomDateFormats.SHORT_WEEKDAY_DAY_MONTH_COMMA) : null;

  return (
    <NavioInfoCard
      density='compact'
      columns={2}
      title={measuredOn ? t`Measured ${measuredOn}` : undefined}
      items={readings.map((reading) => ({
        label: getTirePositionLabel(reading.position),
        value: (
          <span className='inline-flex items-center gap-1.5'>
            {thresholds && (
              <span
                aria-hidden
                className={cn('size-2 rounded-full', TONE_DOT_CLASS[treadDepthTone(reading.valueMm, thresholds)])}
              />
            )}
            {formatMm(reading.valueMm)} mm
          </span>
        ),
        copyText: false as const
      }))}
    >
      {thresholds && (
        <ul className='flex flex-col gap-2 border-t border-border pt-3 text-sm'>
          <LimitRow
            dotClassName={TONE_DOT_CLASS.worn}
            label={getLegalLimitLabel(season)}
            valueMm={thresholds.limitMm}
          />
          <LimitRow
            dotClassName={TONE_DOT_CLASS.soon}
            label={getRecommendedMinimumLabel(season)}
            valueMm={thresholds.recommendedMm}
          />
        </ul>
      )}
    </NavioInfoCard>
  );
};

const LimitRow = ({ dotClassName, label, valueMm }: { dotClassName: string; label: string; valueMm: number }) => (
  <li className='flex items-center justify-between gap-3'>
    <span className='flex items-center gap-2 text-muted-foreground'>
      <span aria-hidden className={cn('size-2 flex-none rounded-full', dotClassName)} />
      {label}
    </span>
    <span className='font-semibold whitespace-nowrap'>{formatMm(valueMm)} mm</span>
  </li>
);

/** Unknown positions, e.g. a middle axle, go last. */
const positionRank = (reading: TreadPositionReadingRecord) => {
  const index = POSITION_ORDER.indexOf(reading.position);
  return index === -1 ? POSITION_ORDER.length : index;
};
